import {
  CircleNotch,
  DownloadSimple,
  type Icon,
  Image,
  Paperclip,
  VideoCamera,
  XCircle,
} from "@phosphor-icons/react";
import clsx from "clsx";

import RenderIf from "common/components/RenderIf";

import { isImageFile, isVideoFile } from "../../../utils";

interface File {
  name: string;
  size: number;
  id?: string;
  url?: string;
}

interface FileAttachmentProps {
  file: File;
  isUploading?: boolean;
  onRemove: () => void;
}

export default function FileAttachment({
  file,
  isUploading = false,
  onRemove,
}: FileAttachmentProps) {
  // Helper function to format file size
  function bytesToMegabytes(bytes: number): string {
    return (bytes / 1024 / 1024).toFixed(2);
  }

  const { name: fileName, size: fileSize } = file;

  // Render icon based on file type
  const renderIcon = () => {
    let IconComponent: Icon;

    if (isUploading) {
      IconComponent = CircleNotch;
    } else if (isImageFile(fileName)) {
      IconComponent = Image;
    } else if (isVideoFile(fileName)) {
      IconComponent = VideoCamera;
    } else {
      IconComponent = Paperclip;
    }

    return (
      <IconComponent
        size={16}
        className={clsx(
          "block text-purple-600",
          isUploading ? "animate-spin" : "group-hover:hidden",
        )}
      />
    );
  };

  // Download file if uploading isn't in progress
  function handleDownload(url: string) {
    if (isUploading || !url) return;
    window.open(url, "_blank");
  }

  return (
    <button
      type="button"
      className="group relative flex w-full flex-row items-center gap-1.5 rounded-2xl border border-black-200 bg-white p-1.5 pr-4 hover:shadow-sm lg:w-auto"
      onClick={() => handleDownload(file.url)}
    >
      <div className="rounded-2xl bg-purple-50 p-2">
        {renderIcon()}

        <RenderIf condition={!isUploading}>
          <DownloadSimple
            size={16}
            className="hidden text-purple-500 group-hover:block"
          />
        </RenderIf>
      </div>

      <div className="flex w-fit flex-col text-start">
        <span className="max-w-[15ch] truncate text-xs-bold">{fileName}</span>

        <span
          className={clsx(
            "text-button-xxs",
            isUploading ? "text-purple-500" : "text-black-500",
          )}
        >
          {isUploading ? "Uploading..." : `${bytesToMegabytes(fileSize)} MB`}
        </span>
      </div>

      <XCircle
        className="lg:ml-none -right-1 -top-0.5 ml-auto cursor-pointer text-black-500 transition-colors group-hover:block hover:text-black-700 lg:absolute lg:hidden"
        weight="fill"
        size={13}
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
      />
    </button>
  );
}
